/**
 * Club de beneficios de la clínica (lógica pura). Cada clínica crea sus planes (individual, doble, familiar o institucional), con beneficios que ella
 * nombra: cupos gratis (valoración, profilaxis…) y descuentos en tratamientos elegidos (porcentaje o precio fijo). El paciente afiliado paga una vez al año.
 * Donna reconoce al afiliado y calcula solo el plan de tratamiento: de todos los beneficios que le tocan aplica el que más le ahorra (no se suman),
 * respetando los cupos de cada integrante. Todos los beneficios se aplican de inmediato (sin meses de espera). Todo el dinero va en centavos.
 */
import { sumarDias } from "./agenda.ts";
import { sumarMeses } from "./mensajes.ts";

export type TipoClub = "individual" | "doble" | "familiar" | "institucional";
export const TIPOS_CLUB: Readonly<Record<TipoClub, { nombre: string; integrantes: number | null; rolIntegrante: "titular" | "empleado" }>> = {
  individual: { nombre: "Individual", integrantes: 1, rolIntegrante: "titular" },
  doble: { nombre: "Doble", integrantes: 2, rolIntegrante: "titular" },
  familiar: { nombre: "Familiar", integrantes: 6, rolIntegrante: "titular" },
  institucional: { nombre: "Institucional (empresa)", integrantes: null, rolIntegrante: "empleado" },
};
export const CLAVES_TIPO_CLUB = Object.keys(TIPOS_CLUB) as TipoClub[];

/**
 * Una forma de vender el mismo plan: individual, doble o familiar, cada una con su precio. La lista de beneficios es la misma para todas.
 * Los planes antiguos (un solo tipo y precio) se ven como una sola modalidad.
 */
export interface ModalidadClub { tipo: Exclude<TipoClub, "institucional">; precio: number; max: number }
export const ORDEN_MODALIDADES: readonly ModalidadClub["tipo"][] = ["individual", "doble", "familiar"];

/** Las modalidades que ofrece un plan: las guardadas o, en un plan antiguo, la única que tenía. Un plan institucional no tiene modalidades. */
export function modalidadesDe(plan: { tipo: TipoClub; precio: number; max_integrantes: number | null; modalidades?: readonly ModalidadClub[] | null }): ModalidadClub[] {
  if (plan.tipo === "institucional") return [];
  if (plan.modalidades?.length) return ORDEN_MODALIDADES.flatMap((t) => plan.modalidades!.filter((m) => m.tipo === t));
  return [{ tipo: plan.tipo, precio: plan.precio, max: plan.max_integrantes ?? TIPOS_CLUB[plan.tipo].integrantes ?? 1 }];
}

export type ClaseBeneficio = "cupo" | "descuento";
export type ModoBeneficio = "gratis" | "porcentaje" | "precio";
export interface BeneficioClub {
  id: string;
  /** como lo llama la clínica: «Valoración gratis», «Profilaxis semestral», «20 % en resinas»… */
  nombre: string;
  clase: ClaseBeneficio;
  /** códigos del arancel a los que aplica */
  codigos: readonly string[];
  modo: ModoBeneficio;
  /** porcentaje (0 a 100) o precio fijo por unidad en centavos; en los cupos no se usa */
  valor: number;
  /** usos gratis por integrante durante la vigencia (solo cupos) */
  cupos: number | null;
}

export function problemasBeneficio(b: Pick<BeneficioClub, "nombre" | "clase" | "codigos" | "modo" | "valor" | "cupos">): string[] {
  const e: string[] = [];
  if (!b.nombre.trim() || b.nombre.trim().length > 80) e.push("El nombre del beneficio debe tener entre 1 y 80 caracteres.");
  if (!["cupo", "descuento"].includes(b.clase)) e.push("Elige si el beneficio es un cupo gratis o un descuento.");
  if (!b.codigos.length) e.push(`«${b.nombre}»: elige al menos un tratamiento.`);
  if (b.clase === "cupo") {
    if (!Number.isInteger(b.cupos) || (b.cupos ?? 0) < 1 || (b.cupos ?? 0) > 100) e.push(`«${b.nombre}»: los usos gratis van de 1 a 100.`);
  } else {
    if (b.modo === "porcentaje" && (!Number.isFinite(b.valor) || b.valor <= 0 || b.valor > 100)) e.push(`«${b.nombre}»: el porcentaje va de 0.01 a 100.`);
    else if (b.modo === "precio" && (!Number.isFinite(b.valor) || b.valor < 0 || b.valor > 100_000_00)) e.push(`«${b.nombre}»: el precio especial no es válido.`);
    else if (!["porcentaje", "precio"].includes(b.modo)) e.push(`«${b.nombre}»: elige porcentaje o precio especial.`);
  }
  return e;
}

const usd = (c: number) => `$${(c / 100).toFixed(2)}`;
/** Cómo se lee el beneficio para el paciente y el personal. */
export function textoBeneficio(b: Pick<BeneficioClub, "clase" | "modo" | "valor" | "cupos">): string {
  const base = b.clase === "cupo" ? `${b.cupos} gratis por integrante` : b.modo === "porcentaje" ? `${b.valor} % de descuento` : `precio especial ${usd(b.valor)}`;
  return base;
}

/* ---------- vigencia ---------- */

export type EstadoAfiliacion = "futura" | "vigente" | "por_vencer" | "vencida" | "anulada";
export const NOMBRE_ESTADO_AFILIACION: Readonly<Record<EstadoAfiliacion, string>> = { futura: "Empieza pronto", vigente: "Vigente", por_vencer: "Por vencer", vencida: "Vencida", anulada: "Anulada" };
export const DIAS_AVISO_RENOVACION = 30;

export const finDeAfiliacion = (inicio: string, vigenciaMeses: number) => sumarDias(sumarMeses(inicio, vigenciaMeses), -1);

export function estadoAfiliacion(a: { inicio: string; fin: string; anulada: boolean }, hoy: string): EstadoAfiliacion {
  if (a.anulada) return "anulada";
  if (hoy < a.inicio) return "futura";
  if (hoy > a.fin) return "vencida";
  return sumarDias(hoy, DIAS_AVISO_RENOVACION) >= a.fin ? "por_vencer" : "vigente";
}

/* ---------- aplicación automática en el plan de tratamiento ---------- */

export interface MembresiaClub { afiliacionId: string; plan: string; inicio: string; fin: string; beneficios: readonly BeneficioClub[] }
/** Unidades gratis ya consumidas de un cupo por este paciente. */
export interface UsoClub { afiliacionId: string; beneficioId: string; unidades: number }
export interface ItemParaClub { id: string; codigo: string; precio: number; cantidad: number }
export interface AplicacionClub {
  itemId: string; afiliacionId: string; beneficioId: string; nombre: string; plan: string; clase: ClaseBeneficio;
  /** descuento efectivo sobre el valor de lista del procedimiento (0 a 100, exacto a 6 decimales) */
  pct: number;
  /** unidades gratis que consume (solo cupos) */
  unidadesGratis: number;
  /** valor de lista cubierto por el cupo (la clínica lo asume) */
  valorCubierto: number;
  /** cuánto se ahorra el paciente frente al precio de lista */
  ahorro: number;
}

const redondear6 = (n: number) => Math.round(n * 1e6) / 1e6;

/**
 * Qué beneficio del club aplica a cada procedimiento pendiente. Solo se aplica el MAYOR descuento (no se suman) y solo si supera el descuento general
 * del plan. Los cupos se reparten en el orden de los procedimientos y se descuentan los ya usados. 
 */
export function aplicarClub(items: readonly ItemParaClub[], membresias: readonly MembresiaClub[], usos: readonly UsoClub[], descuentoPlanPct: number, hoy: string): Map<string, AplicacionClub> {
  const usados = new Map<string, number>();
  for (const u of usos) usados.set(`${u.afiliacionId}|${u.beneficioId}`, (usados.get(`${u.afiliacionId}|${u.beneficioId}`) ?? 0) + u.unidades);
  const res = new Map<string, AplicacionClub>();
  for (const it of items) {
    if (it.precio <= 0 || it.cantidad <= 0) continue;
    const codigo = it.codigo.toUpperCase();
    let mejor: AplicacionClub | null = null;
    for (const m of membresias) {
      if (hoy < m.inicio || hoy > m.fin) continue;
      for (const b of m.beneficios) {
        if (!b.codigos.includes(codigo)) continue;
        let pct = 0, gratis = 0;
        if (b.clase === "cupo") {
          const resta = Math.max(0, (b.cupos ?? 0) - (usados.get(`${m.afiliacionId}|${b.id}`) ?? 0));
          gratis = Math.min(it.cantidad, resta);
          pct = (gratis / it.cantidad) * 100;
        } else if (b.modo === "porcentaje") pct = b.valor;
        else pct = Math.max(0, (1 - b.valor / it.precio) * 100);
        pct = redondear6(Math.min(100, pct));
        if (pct <= descuentoPlanPct + 1e-9) continue;
        const cand: AplicacionClub = { itemId: it.id, afiliacionId: m.afiliacionId, beneficioId: b.id, nombre: b.nombre, plan: m.plan, clase: b.clase, pct, unidadesGratis: gratis,
          valorCubierto: gratis > 0 ? Math.round(it.precio * gratis) : 0, ahorro: Math.round((it.precio * it.cantidad * pct) / 100) };
        // gana el mayor descuento; si empatan, el que no gasta un cupo
        if (!mejor || cand.pct > mejor.pct + 1e-9 || (Math.abs(cand.pct - mejor.pct) <= 1e-9 && mejor.clase === "cupo" && cand.clase === "descuento")) mejor = cand;
      }
    }
    if (mejor) {
      res.set(it.id, mejor);
      if (mejor.unidadesGratis > 0) { const k = `${mejor.afiliacionId}|${mejor.beneficioId}`; usados.set(k, (usados.get(k) ?? 0) + mejor.unidadesGratis); }
    }
  }
  return res;
}

/** Lo que le queda a un integrante de cada cupo de su afiliación (para mostrar en la ficha y en la lista). */
export function cuposRestantes(b: Pick<BeneficioClub, "id" | "clase" | "cupos">, afiliacionId: string, usos: readonly UsoClub[]): number | null {
  if (b.clase !== "cupo") return null;
  const usadas = usos.filter((u) => u.afiliacionId === afiliacionId && u.beneficioId === b.id).reduce((s, u) => s + u.unidades, 0);
  return Math.max(0, (b.cupos ?? 0) - usadas);
}
